// Client side of the news search (docs/architecture.md, "Search"): lazily
// loads the Pagefind index that `bun run build` writes to dist/client/pagefind
// and runs queries against it. Nothing here is imported until a visitor
// focuses the search box or opens a `?q=` URL.
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { NewsType } from './types'

type PagefindData = {
  url: string
  /** HTML; Pagefind escapes the text and wraps matches in <mark>. */
  excerpt: string
  meta: {
    title?: string
    date?: string
    label?: string
    outlet?: string
  }
}
type PagefindResult = { id: string; data: () => Promise<PagefindData> }
type Pagefind = {
  search: (
    query: string,
    options?: { filters?: Record<string, string> },
  ) => Promise<{ results: PagefindResult[] }>
}

export type SearchHit = PagefindData & { id: string }

export const SEARCH_PAGE_SIZE = 10

let pagefind: Promise<Pagefind> | undefined

/** Loads (once) the Pagefind runtime; the index fragments load per query. */
export function loadPagefind(): Promise<Pagefind> {
  if (!pagefind) {
    // A variable URL keeps Vite from trying to bundle a file that only exists
    // after the postbuild step.
    const url = '/pagefind/pagefind.js'
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- Pagefind's runtime is generated after the build, so it has no types
    pagefind = (import(/* @vite-ignore */ url) as Promise<Pagefind>).catch(
      (e: unknown) => {
        pagefind = undefined
        throw e
      },
    )
  }
  return pagefind
}

type SearchResults = { hits: SearchHit[]; total: number }

/** Runs a query and loads the excerpts of its first `limit` hits. */
async function searchNews(
  query: string,
  tab: NewsType,
  limit: number,
): Promise<SearchResults> {
  const pf = await loadPagefind()
  const { results } = await pf.search(
    query,
    tab === 'all' ? {} : { filters: { type: tab } },
  )
  const hits = await Promise.all(
    results
      .slice(0, limit)
      .map(async (r) => ({ ...(await r.data()), id: r.id })),
  )
  return { hits, total: results.length }
}

// The index only changes with a deploy, so results are never stale within a
// session. No retry: without an index (`bun dev`) it fails for good.
const newsSearchQuery = (query: string, tab: NewsType, limit: number) =>
  queryOptions({
    queryKey: ['news-search', tab, query, limit],
    queryFn: () => searchNews(query, tab, limit),
    staleTime: Infinity,
    retry: false,
  })

export type NewsSearchState = {
  status: 'idle' | 'loading' | 'ready' | 'error'
  hits: SearchHit[]
  total: number
  /** Whether there are more results than `hits`. */
  more: boolean
  showMore: () => void
}

/**
 * Results of `query` within one news tab ('all' searches everything). Empty
 * query is `idle`. The tab id doubles as the Pagefind `type` filter value.
 */
export function useNewsSearch(query: string, tab: NewsType): NewsSearchState {
  const key = `${tab}\n${query}`
  const [shown, setShown] = useState({ key, limit: SEARCH_PAGE_SIZE })
  // A new search starts from the first ten hits again.
  if (shown.key !== key) setShown({ key, limit: SEARCH_PAGE_SIZE })
  const limit = shown.key === key ? shown.limit : SEARCH_PAGE_SIZE

  const search = useQuery({
    ...newsSearchQuery(query, tab, limit),
    enabled: Boolean(query),
    // Keep the hits on screen while "Show more" loads the next ten, but not
    // those of a different search.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === tab && previousQuery.queryKey[2] === query
        ? previous
        : undefined,
  })

  const status = !query
    ? 'idle'
    : search.isError
      ? 'error'
      : search.isPlaceholderData || search.isPending
        ? 'loading'
        : 'ready'
  const total = search.data?.total ?? 0
  return {
    status,
    hits: search.data?.hits ?? [],
    total,
    more: total > limit,
    showMore: () => setShown({ key, limit: limit + SEARCH_PAGE_SIZE }),
  }
}
